import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Button, 
  Tag, 
  Typography, 
  List, 
  Space, 
  Switch, 
  Modal, 
  Form, 
  Input, 
  InputNumber, 
  Select,
  message,
  Badge,
  Tooltip,
  Collapse,
  Alert
} from 'antd';
import { 
  CrownOutlined, 
  EditOutlined, 
  CheckOutlined,
  PlusOutlined
} from '@ant-design/icons';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { SubscriptionPlan, adminApi, TierLimitationsResponse, TierLimitation } from '../lib/api';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TextArea } = Input;

// Supported placeholders for dynamic tier field replacement
// These map to TierLimitation fields and will be replaced with actual tier values
const SUPPORTED_PLACEHOLDERS = [
  // Numeric limits
  { placeholder: '{{closet_limit}}', description: 'Maximum closet items', field: 'closet_limit' },
  { placeholder: '{{max_photo_models}}', description: 'Maximum photo models', field: 'max_photo_models' },
  { placeholder: '{{max_smart_add_per_day}}', description: 'Smart adds per day', field: 'max_smart_add_per_day' },
  { placeholder: '{{max_try_on_per_day}}', description: 'Try-ons per day', field: 'max_try_on_per_day' },
  { placeholder: '{{max_ai_stylist_per_day}}', description: 'AI stylist requests per day', field: 'max_ai_stylist_per_day' },
  { placeholder: '{{max_smart_tryoff_per_day}}', description: 'Smart try-offs per day', field: 'max_smart_tryoff_per_day' },
  { placeholder: '{{max_batch_processing_size}}', description: 'Batch processing size', field: 'max_batch_processing_size' },
  { placeholder: '{{cloth_item_per_tryon}}', description: 'Cloth items per try-on', field: 'cloth_item_per_tryon' },
  { placeholder: '{{custom_ai_models}}', description: 'Custom AI models', field: 'custom_ai_models' },
  { placeholder: '{{free_zar}}', description: 'Free ZAR credits', field: 'free_zar' },
  { placeholder: '{{saved_recommendations_limit}}', description: 'Saved recommendations limit', field: 'saved_recommendations_limit' },
  { placeholder: '{{saved_looks_limit}}', description: 'Saved looks limit', field: 'saved_looks_limit' },
  
  // Boolean features (will show as Yes/No or Enabled/Disabled)
  { placeholder: '{{custom_prompt_in_try_on}}', description: 'Custom prompts in try-on', field: 'custom_prompt_in_try_on' },
  { placeholder: '{{quick_ai_stylist}}', description: 'Quick AI stylist', field: 'quick_ai_stylist' },
  { placeholder: '{{advanced_ai_stylist}}', description: 'Advanced AI stylist', field: 'advanced_ai_stylist' },
  { placeholder: '{{init_2_ai_models}}', description: 'Initial 2 AI models', field: 'init_2_ai_models' },
  { placeholder: '{{profile_completion_3_ai_models}}', description: 'Profile completion 3 AI models', field: 'profile_completion_3_ai_models' },
  { placeholder: '{{ai_stylist_save}}', description: 'AI stylist save feature', field: 'ai_stylist_save' },
  { placeholder: '{{ai_style_quick_recom}}', description: 'AI style quick recommendations', field: 'ai_style_quick_recom' },
  { placeholder: '{{add_cloth_item_single}}', description: 'Add cloth item (single)', field: 'add_cloth_item_single' },
  { placeholder: '{{add_cloth_item_batch}}', description: 'Add cloth item (batch)', field: 'add_cloth_item_batch' },
  { placeholder: '{{upload_photo_model}}', description: 'Upload photo model', field: 'upload_photo_model' },
  { placeholder: '{{tryon}}', description: 'Try-on feature', field: 'tryon' },
];

interface SubscriptionPlansProps {
  plans?: SubscriptionPlan[];
  loading?: boolean;
}

const SubscriptionPlans: React.FC<SubscriptionPlansProps> = ({ plans = [], loading = false }) => {
  const [editingPlan, setEditingPlan] = useState<SubscriptionPlan | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();
  const featuresValue = Form.useWatch('features', form) || '';
  const queryClient = useQueryClient();

  // Fetch tier limitations for dynamic placeholder replacement
  const { data: tierLimitationsData } = useQuery<TierLimitationsResponse>({
    queryKey: ['tier-limitations'],
    queryFn: () => adminApi.getTierLimitations(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const updatePlanMutation = useMutation({
    mutationFn: ({ tier, plan }: { tier: string; plan: Partial<SubscriptionPlan> }) => {
      console.log('DEBUG: Mutation called with:', { tier, plan });
      return adminApi.updateSubscriptionPlan(tier, plan);
    },
    onSuccess: (data) => {
      console.log('DEBUG: Mutation successful, response:', data);
      message.success('Plan updated successfully');
      queryClient.invalidateQueries({ queryKey: ['subscription-plans'] });
      setModalVisible(false);
      setEditingPlan(null);
      form.resetFields();
    },
    onError: (error: any) => {
      console.error('DEBUG: Mutation failed:', error);
      message.error('Failed to update plan: ' + (error.message || 'Unknown error'));
    }
  });

  const getTierColor = (tier: string) => {
    const colors: Record<string, string> = {
      'free': 'default',
      'bronze': '#CD7F32',
      'silver': '#C0C0C0', 
      'gold': '#FFD700',
      'firooze': '#40E0D0'
    };
    return colors[tier] || 'blue';
  };

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const formatLimit = (value: number | undefined, unit: string = '') => {
    if (value === undefined) return 'Unlimited';
    return `${value}${unit}`;
  };

  // Replace placeholders in feature text with actual tier limitation values
  const replacePlaceholders = (text: string, tier: string): string => {
    if (!tierLimitationsData?.data?.tier_limitations?.[tier]) {
      return text;
    }

    const tierLimits = tierLimitationsData.data.tier_limitations[tier];
    let replacedText = text;

    // Replace each supported placeholder
    SUPPORTED_PLACEHOLDERS.forEach(({ placeholder, field }) => {
      const value = tierLimits[field];
      let displayValue: string;

      if (value === null || value === undefined) {
        displayValue = 'Unlimited';
      } else if (typeof value === 'boolean') {
        displayValue = value ? '✓' : '✗';
      } else {
        displayValue = String(value);
      }

      // Replace all occurrences of the placeholder
      replacedText = replacedText.replace(new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'), displayValue);
    });

    return replacedText;
  };

  const handleEdit = (plan: SubscriptionPlan) => {
    console.log('DEBUG: handleEdit called with plan:', {
      tier: plan.tier,
      features: plan.features,
      featuresType: typeof plan.features,
      isArray: Array.isArray(plan.features)
    });
    setEditingPlan(plan);
    setModalVisible(true);
  };

  // Update form values when editingPlan changes
  useEffect(() => {
    if (editingPlan && modalVisible) {
      console.log('DEBUG: useEffect triggered, editingPlan:', {
        tier: editingPlan.tier,
        features: editingPlan.features,
        featuresType: typeof editingPlan.features,
        isArray: Array.isArray(editingPlan.features)
      });
      
      // Convert features array to string explicitly
      let featuresText = '';
      if (Array.isArray(editingPlan.features)) {
        featuresText = editingPlan.features.length > 0 
          ? editingPlan.features.join('\n')
          : '';
      } else if (typeof editingPlan.features === 'string') {
        featuresText = editingPlan.features;
      }
      
      console.log('DEBUG: Converted features to text:', {
        original: editingPlan.features,
        converted: featuresText,
        length: featuresText.length
      });
      
      // Set form values explicitly, don't spread editingPlan to avoid array features
      // Handle both old format (price_monthly/price_annual) and new format (price.monthly/price.annual)
      const priceMonthly = (editingPlan as any).price?.monthly ?? editingPlan.price_monthly ?? 0;
      const priceAnnual = (editingPlan as any).price?.annual ?? editingPlan.price_annual ?? 0;
      
      const formValues: any = {
        display_name: editingPlan.display_name || '',
        description: editingPlan.description || '',
        price_monthly: priceMonthly,
        price_annual: priceAnnual,
        currency: editingPlan.currency || 'USD',
        is_active: editingPlan.is_active ?? true,
        sort_order: editingPlan.sort_order ?? 0,
        features: featuresText // Explicitly set as string
      };
      
      // Reset form first to clear any stale values
      form.resetFields();
      
      // Set form values after a brief delay to ensure reset completes
      setTimeout(() => {
        form.setFieldsValue(formValues);
        console.log('DEBUG: Form values set:', formValues);
        
        // Set features field explicitly again to ensure it's bound
        form.setFieldValue('features', featuresText);
        console.log('DEBUG: Features field set explicitly to:', featuresText);
        
        // Verify the value was set multiple times to ensure it sticks
        setTimeout(() => {
          const verifiedValue = form.getFieldValue('features');
          const allValues = form.getFieldsValue();
          console.log('DEBUG: Form initialization complete:', {
            tier: editingPlan.tier,
            originalFeatures: editingPlan.features,
            featuresText: featuresText,
            formFeaturesValue: formValues.features,
            verifiedFeaturesValue: verifiedValue,
            verifiedType: typeof verifiedValue,
            match: verifiedValue === featuresText,
            allFormValues: allValues
          });
          
          if (verifiedValue !== featuresText) {
            console.warn('WARNING: Form features value mismatch!', {
              expected: featuresText,
              actual: verifiedValue,
              actualType: typeof verifiedValue
            });
            // Try setting again explicitly multiple times
            form.setFieldValue('features', featuresText);
            setTimeout(() => {
              form.setFieldValue('features', featuresText);
              const retryValue = form.getFieldValue('features');
              console.log('DEBUG: After multiple retry sets:', retryValue);
            }, 50);
          } else {
            console.log('DEBUG: Form value verified successfully!');
          }
        }, 100);
      }, 100);
    }
  }, [editingPlan, modalVisible]);

  const handleSave = async () => {
    console.log('DEBUG: handleSave called');
    if (!editingPlan) {
      console.error('No plan selected for editing');
      message.error('No plan selected');
      return;
    }

    try {
      console.log('DEBUG: Validating form fields...');
      
      // Get features field value directly from form before validation
      const currentFeaturesValue = form.getFieldValue('features');
      console.log('DEBUG: Current features field value:', currentFeaturesValue);
      console.log('DEBUG: Current features field type:', typeof currentFeaturesValue);
      
      const values = await form.validateFields();
      console.log('DEBUG: Raw form values after validation:', values);
      console.log('DEBUG: values.features type:', typeof values.features);
      console.log('DEBUG: values.features value:', values.features);
      
      // Get features directly from form - ensure it's a string, not an array
      // Use the validated value, but fallback to direct field value
      let featuresText = '';
      const featuresValue = values.features !== undefined ? values.features : currentFeaturesValue;
      
      if (typeof featuresValue === 'string') {
        featuresText = featuresValue;
      } else if (Array.isArray(featuresValue)) {
        // If somehow it's an array, convert to string
        featuresText = featuresValue.join('\n');
        console.warn('WARNING: Features value was an array, converted to string');
      } else if (featuresValue === null || featuresValue === undefined) {
        featuresText = '';
      } else {
        featuresText = String(featuresValue);
      }
      
      const featuresArray = featuresText 
        ? featuresText.split('\n').filter((f: string) => f.trim()) 
        : [];
      
      // Build updated plan explicitly - only include fields we want, exclude features from spread
      const { features: _, ...otherValues } = values;
      
      const updatedPlan: Partial<SubscriptionPlan> = {
        display_name: values.display_name,
        description: values.description,
        price_monthly: values.price_monthly,
        price_annual: values.price_annual,
        currency: values.currency || 'USD',
        is_active: values.is_active,
        sort_order: values.sort_order,
        features: featuresArray  // Explicitly set features as array from textarea
      };
      
      console.log('DEBUG: Preparing to send update:', {
        tier: editingPlan.tier,
        featuresFromForm: values.features,
        featuresText: featuresText,
        featuresArray: featuresArray,
        updatedPlanFeatures: updatedPlan.features,
        fullUpdatedPlan: updatedPlan
      });
      
      updatePlanMutation.mutate({
        tier: editingPlan.tier,
        plan: updatedPlan
      });
    } catch (error: any) {
      console.error('Form validation or save failed:', error);
      if (error?.errorFields) {
        // Form validation errors
        console.error('Validation errors:', error.errorFields);
        message.error('Please fix form errors before saving');
      } else {
        message.error('Failed to save: ' + (error?.message || 'Unknown error'));
      }
    }
  };

  const handleToggleActive = (plan: SubscriptionPlan) => {
    updatePlanMutation.mutate({
      tier: plan.tier,
      plan: { is_active: !plan.is_active }
    });
  };

  const planCards = plans.map((plan) => (
    <Col xs={24} sm={12} lg={8} key={plan.tier}>
      <Card
        style={{ height: '100%' }}
        title={
          <Space align="center">
            <Badge dot={!plan.is_active} status={plan.is_active ? 'success' : 'default'}>
              <Title level={4} style={{ margin: 0, color: getTierColor(plan.tier) }}>
                <CrownOutlined /> {plan.display_name}
              </Title>
            </Badge>
            <Tag color={getTierColor(plan.tier)} style={{ marginLeft: 'auto' }}>
              {plan.tier.toUpperCase()}
            </Tag>
          </Space>
        }
        extra={
          <Space>
            <Tooltip title={plan.is_active ? 'Disable Plan' : 'Enable Plan'}>
              <Switch
                checked={plan.is_active}
                onChange={() => handleToggleActive(plan)}
                loading={updatePlanMutation.isPending}
                size="small"
              />
            </Tooltip>
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(plan)}
              size="small"
            />
          </Space>
        }
        actions={[
          <div key="pricing">
            <Text strong style={{ color: '#1890ff' }}>
              {(() => {
                // Handle both old format and new nested format
                const monthly = (plan as any).price?.monthly ?? plan.price_monthly ?? 0;
                return monthly > 0 ? formatCurrency(monthly) + '/mo' : 'Free';
              })()}
            </Text>
            <br />
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {(() => {
                const annual = (plan as any).price?.annual ?? plan.price_annual ?? 0;
                return annual > 0 ? formatCurrency(annual) + '/yr' : '';
              })()}
            </Text>
          </div>
        ]}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {plan.description && (
            <Paragraph type="secondary" style={{ marginBottom: '12px' }}>
              {plan.description}
            </Paragraph>
          )}

          {/* Features List */}
          <div>
            <Title level={5} style={{ marginBottom: '8px' }}>Features</Title>
            <List
              size="small"
              dataSource={plan.features.slice(0, 6)} // Show first 6 features
              renderItem={(feature) => {
                const displayFeature = replacePlaceholders(feature, plan.tier);
                return (
                  <List.Item style={{ padding: '2px 0' }}>
                    <Text style={{ fontSize: '12px' }}>
                      <CheckOutlined style={{ color: '#52c41a', marginRight: '4px' }} />
                      {displayFeature}
                    </Text>
                  </List.Item>
                );
              }}
            />
            {plan.features.length > 6 && (
              <Text type="secondary" style={{ fontSize: '12px' }}>
                +{plan.features.length - 6} more features
              </Text>
            )}
          </div>
        </Space>
      </Card>
    </Col>
  ));

  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
        <Col span={24}>
          <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
            <div>
              <Title level={3} style={{ margin: 0 }}>Subscription Plans</Title>
              <Text type="secondary">Manage pricing and features for each plan tier</Text>
            </div>
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              disabled // Placeholder for adding new plans
            >
              Add New Plan
            </Button>
          </Space>
        </Col>
      </Row>

      <Row gutter={[16, 16]} justify="start">
        {loading ? (
          // Loading skeletons
          Array.from({ length: 5 }, (_, i) => (
            <Col xs={24} sm={12} lg={8} key={i}>
              <Card loading={true} style={{ height: '400px' }} />
            </Col>
          ))
        ) : (
          planCards
        )}
      </Row>

      {/* Edit Plan Modal */}
      <Modal
        title={`Edit ${editingPlan?.display_name || 'Plan'}`}
        open={modalVisible}
        onOk={async () => {
          console.log('DEBUG: Modal onOk clicked');
          await handleSave();
        }}
        onCancel={() => {
          form.resetFields();
          setModalVisible(false);
          setEditingPlan(null);
        }}
        confirmLoading={updatePlanMutation.isPending}
        width={600}
        okText="Save"
        cancelText="Cancel"
      >
        <Form
          form={form}
          layout="vertical"
          preserve={false}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Display Name"
                name="display_name"
                rules={[{ required: true, message: 'Please enter display name' }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Currency"
                name="currency"
                rules={[{ required: false }]}
                initialValue="USD"
              >
                <Select>
                  <Option value="USD">USD</Option>
                  <Option value="EUR">EUR</Option>
                  <Option value="GBP">GBP</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Description"
            name="description"
          >
            <TextArea rows={2} />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Monthly Price"
                name="price_monthly"
                rules={[{ type: 'number', min: 0 }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  step={0.01}
                  precision={2}
                  addonBefore="$"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Annual Price"
                name="price_annual"
                rules={[{ type: 'number', min: 0 }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  step={0.01}
                  precision={2}
                  addonBefore="$"
                />
              </Form.Item>
            </Col>
          </Row>

          <Alert
            message="Tier Limitations"
            description="Tier limitations (closet size, try-ons per day, etc.) are managed in the 'Tier Limitations' tab. Use placeholders in the features below to reference them dynamically."
            type="info"
            showIcon
            style={{ marginBottom: '16px' }}
          />

          <Form.Item
            label="Features (one per line)"
            name="features"
            help="Use {{placeholder}} syntax to dynamically include tier limits. These values come from the Tier Limitations tab."
            rules={[]}
            preserve={false}
            initialValue=""
          >
            <TextArea 
              rows={6} 
              value={featuresValue}
              onChange={(e) => {
                form.setFieldValue('features', e.target.value);
              }}
              placeholder={`Enter each feature on a new line
Example: Up to {{closet_limit}} closet items
Example: {{max_smart_add_per_day}} smart adds per day
Example: {{max_try_on_per_day}} try-ons per day
Example: Advanced AI: {{advanced_ai_stylist}}`}
            />
            <Collapse 
              ghost 
              style={{ marginTop: '8px' }}
              items={[
                {
                  key: '1',
                  label: <Text type="secondary" style={{ fontSize: '12px' }}>Click to view supported placeholders</Text>,
                  children: (
                    <div style={{ marginTop: '8px' }}>
                      <Alert
                        message="Supported Placeholders"
                        description={
                          <div style={{ marginTop: '8px' }}>
                            <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                              Use these placeholders in your feature text. They will be automatically replaced with actual tier values.
                            </Text>
                            <List
                              size="small"
                              bordered
                              dataSource={SUPPORTED_PLACEHOLDERS}
                              renderItem={(item) => (
                                <List.Item style={{ padding: '8px 12px' }}>
                                  <Space>
                                    <Text code style={{ fontSize: '11px', background: '#f0f0f0' }}>{item.placeholder}</Text>
                                    <Text type="secondary" style={{ fontSize: '11px' }}>{item.description}</Text>
                                  </Space>
                                </List.Item>
                              )}
                            />
                            <Text type="secondary" style={{ fontSize: '11px', display: 'block', marginTop: '8px' }}>
                              <strong>Note:</strong> Values showing as null will display as "Unlimited" in the frontend.
                            </Text>
                          </div>
                        }
                        type="info"
                        style={{ fontSize: '12px' }}
                      />
                    </div>
                  )
                }
              ]}
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Sort Order"
                name="sort_order"
                rules={[{ type: 'number', min: 0 }]}
              >
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Active"
                name="is_active"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default SubscriptionPlans;